import { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import Wrapper from "../../helpers/Wrapper";

am5.addLicense("AM5C258896422");

const GroupedBarChart = ({ chartId, chartData }) => {
    useLayoutEffect(() => {
        // Destructure chartData
        const { data, label } = chartData;

        // Create root element
        const root = am5.Root.new(chartId);

        // Set themes
        root.setThemes([am5themes_Animated.new(root)]);

        // Create chart
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "panX",
                wheelY: "zoomX",
                paddingLeft: 0,
                layout: root.verticalLayout,
            })
        );

        // Add scrollbar
        chart.set(
            "scrollbarX",
            am5.Scrollbar.new(root, {
                orientation: "horizontal",
            })
        );

        // Create axes
        const xRenderer = am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
            cellStartLocation: 0.4,
            cellEndLocation: 0.6,
        });

        // Remove x-axis grid lines but keep the axis line
        xRenderer.grid.template.set("visible", false);

        const xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: "label",
                renderer: xRenderer,
                tooltip: am5.Tooltip.new(root, {}),
            })
        );

        xAxis.data.setAll(data);

        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                strictMinMax: false,
                calculateTotals: true,
                renderer: am5xy.AxisRendererY.new(root, {
                    strokeOpacity: 0.1,
                }),
            })
        );

        yAxis.get("renderer").grid.template.set("visible", true);

        // Add legend
        const legend = chart.children.push(
            am5.Legend.new(root, {
                centerX: am5.p50,
                x: am5.p50,
            })
        );

        // Add series dynamically based on label data
        label.forEach((item) => {
            const series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: item.label,
                    stacked: false,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: item.value,
                    categoryXField: "label",
                })
            );

            series.columns.template.setAll({
                tooltipText: "{name}, {categoryX}:{valueY}",
                cornerRadiusTL: 6,
                cornerRadiusTR: 6,
                width: 10,
            });

            series.data.setAll(data);
            series.appear();
            legend.data.push(series);
        });

        // Make stuff animate on load
        chart.appear(1000, 100);

        // Cleanup on unmount
        return () => {
            root.dispose();
        };
    }, [chartId, chartData]);

    return (
        <Wrapper>
            <div
                id={chartId}
                style={{
                    height: "500px",
                }}
            ></div>
        </Wrapper>
    );
};

// Prop validation
GroupedBarChart.propTypes = {
    chartId: PropTypes.string.isRequired,
    chartData: PropTypes.shape({
        data: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.number.isRequired,
            })
        ).isRequired,
        label: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.string.isRequired,
            })
        ).isRequired,
    }).isRequired,
};

export default GroupedBarChart;